import React, { Fragment, useState, useEffect } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Grid, IconButton, Chip } from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import MUIDataTable from "mui-datatables";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";
import { setCurrentPage } from "actions";

const getMuiTheme = () =>
  createMuiTheme({
    overrides: {
      MUIDataTable: {
        filterPaper: {
          width: "500px", // フィルターダイアログの幅を500pxに設定
        },
      },
    },
  });

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      display: "flex",
      position: "fixed",
      backgroundColor: "#fafafa",
      zIndex: 1000,
      width: "100%",
    },
    alignLeft: {
      lineHeight: "18px",
      textAlign: "left",
    },
    groupIdCell: {
      width: "18%",
      whiteSpace: "nowrap",
      textAlign: "left",
    },
    groupNameCell: {
      width: "16%",
      whiteSpace: "nowrap",
      textAlign: "left",
    },
    playerCountCell: {
      width: "8%",
      whiteSpace: "nowrap",
      textAlign: "center",
    },
    playerIdsCell: {
      width: "100%",
      whiteSpace: "normal",
      wordBreak: "break-word",
      lineHeight: "18px",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },
    referralCodeCell: {
      width: "100%",
      whiteSpace: "nowrap",
      textAlign: "center",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    summaryCenter: {
      textAlign: "center",
    },
    cvrHead: {
      textAlign: "center",
      fontSize: 12,
      fontWeight: "normal",
      borderBottom: "1px solid rgba(224, 224, 224, 1)",
    },
    flexItemLeftBox: {
      marginTop: 28,
      alignSelf: "center",
      paddingLeft: 8,
      paddingRight: 8,
      borderRight: "1px solid #dcdcdc",
    },
  })
);

const GroupTable = ({
  setCurrentPage,
  tableRowSelected,
  groupSummaryData,
}) => {
  const classes = useStyles();

  useEffect(() => {
    setCurrentPage(window.location.pathname);
  }, []);

  const getGroupSummaryData = (data) => {
    if (data.length > 0) {
      let allDataArray = [],
        eachDataArray = [];
      for (let i = 0; i < data.length; i++) {
        eachDataArray.push(data[i]["groupId"]);
        eachDataArray.push(data[i]["groupName"]);
        eachDataArray.push(data[i]["playerCount"]);
        eachDataArray.push(data[i]["playerIds"] || "");
        eachDataArray.push(data[i]["referralCode"] || "");
        allDataArray.push(eachDataArray);
        eachDataArray = [];
      }
      return allDataArray;
    }
  };

  const getColumns = () => {
    let returningColumn = columns;
    if (
      localStorage.getItem("access_key") !== process.env.REACT_APP_SUPERUSER
    ) {
      returningColumn.pop();
    }
    return returningColumn;
  };

  const options = {
    filterType: "dropdown",
    filter: true,
    pagination: true,
    search: true,
    print: false,
    download: true,
    downloadOptions: {
      filename: "登録グループ一括データ",
      filterOptions: {
        useDisplayedColumnsOnly: true,
        useDisplayedRowsOnly: true,
      },
    },
    selectableRows: "none",
    rowsPerPage: 100,
    rowsSelected: tableRowSelected,
    textLabels: {
      body: {
        noMatch: "データを取得中です。",
        toolTip: "",
      },
      toolbar: {
        search: "検索",
        viewColumns: "列の表示",
        downloadCsv: "CSVダウンロード",
        filterTable: "テーブルのフィルター",
      },
      viewColumns: {
        title: "表示する列",
        titleAria: "テーブルの列の表示/非表示",
      },
      pagination: {
        next: "次のページ",
        previous: "前のページ",
        rowsPerPage: "表示件数",
      },
      filter: {
        all: "全て",
        title: "フィルター",
        reset: "リセット",
      },
      selectedRows: {
        text: "レコードを選択",
        delete: "削除",
        deleteAria: "選択したレコードを削除する",
      },
    },
    rowsPerPageOptions: [10, 50, 100],
  };

  let columns = [
    {
      name: "グループID",
      options: {
        filter: true,
        customFilterListRender: (v) => `グループID: ${v}`,
        customHeadLabelRender: () => (
          <div className={classes.groupIdCell}>グループID</div>
        ),
        setCellHeaderProps: () => ({
          style: {
            width: "18%",
            whiteSpace: "nowrap",
          },
        }),
        setCellProps: () => ({
          style: {
            width: "18%",
            whiteSpace: "nowrap",
          },
        }),
        customBodyRender: (value) => {
          return <div className={classes.groupIdCell}>{value}</div>;
        },
      },
    },
    {
      name: "グループ名",
      options: {
        filter: true,
        customFilterListRender: (v) => `グループ名: ${v}`,
        customHeadLabelRender: () => (
          <div className={classes.groupNameCell}>グループ名</div>
        ),
        setCellHeaderProps: () => ({
          style: {
            width: "16%",
            whiteSpace: "nowrap",
          },
        }),
        setCellProps: () => ({
          style: {
            width: "16%",
            whiteSpace: "nowrap",
          },
        }),
        customBodyRender: (value) => {
          return <div className={classes.groupNameCell}>{value}</div>;
        },
      },
    },
    {
      name: "登録数",
      options: {
        filter: false,
        customFilterListRender: (v) => `登録数: ${v}`,
        setCellHeaderProps: () => ({
          style: {
            width: "8%",
            whiteSpace: "nowrap",
          },
        }),
        setCellProps: () => ({
          style: {
            width: "8%",
            whiteSpace: "nowrap",
          },
        }),
        customBodyRender: (value) => {
          return <div className={classes.playerCountCell}>{value}</div>;
        },
      },
    },
    {
      name: "プレイヤーID",
      options: {
        filter: false,
        customFilterListRender: (v) => `プレイヤーID: ${v}`,
        customHeadLabelRender: () => (
          <div style={{ whiteSpace: "nowrap", textAlign: "center" }}>プレイヤーID</div>
        ),
        setCellHeaderProps: () => ({
          style: {
            width: "38%",
            whiteSpace: "nowrap",
            textAlign: "center",
          },
        }),
        setCellProps: () => ({
          style: {
            width: "38%",
            whiteSpace: "normal",
            wordBreak: "break-word",
            textAlign: "center",
          },
        }),
        customBodyRender: (value) => {
          const playerIds = typeof value === "string" ? value : "";
          const lines = playerIds
            .split(/,\s*/)
            .map((id) => id.trim())
            .filter(Boolean);

          return (
            <div className={classes.playerIdsCell}>
              {lines.map((id, index) => (
                <React.Fragment key={`${id}-${index}`}>
                  {id}
                  {index < lines.length - 1 ? <br /> : null}
                </React.Fragment>
              ))}
            </div>
          );
        },
      },
    },
    {
      name: "招待コード",
      options: {
        filter: true,
        customFilterListRender: (v) => `招待コード: ${v}`,
        customHeadLabelRender: () => (
          <div className={classes.referralCodeCell}>招待コード</div>
        ),
        setCellHeaderProps: () => ({
          style: {
            width: "24%",
            whiteSpace: "nowrap",
            textAlign: "center",
          },
        }),
        setCellProps: () => ({
          style: {
            width: "24%",
            whiteSpace: "nowrap",
            textAlign: "center",
          },
        }),
        customBodyRender: (value) => {
          return <div className={classes.referralCodeCell}>{value}</div>;
        },
      },
    },
  ];

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <MUIDataTable
            title={`登録グループ：${groupSummaryData?.groupCount || 0}件`}
            data={getGroupSummaryData(groupSummaryData?.groupData || [])}
            columns={getColumns()}
            options={options}
          />
        </Grid>
      </Grid>
    </>
  );
};

GroupTable.propTypes = {
  classes: PropTypes.object,
  groupSummaryData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

const mapStateToProps = (state) => ({
  tableRowSelected: state.page.tableRowSelected,
  groupSummaryData: state.pictreeHokuden.groupSummaryData,
});

const mapDispatchToProps = (dispatch) => ({
  setCurrentPage: (param) => dispatch(setCurrentPage(param)),
});

export default connect(mapStateToProps, mapDispatchToProps)(GroupTable);
